import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IonButton } from '@ionic/react';
import { IState, ITabProperties } from 'appRedux/createStore';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { FilterTypeEnum, IFilterType, IGraphData } from 'common/common';
import { playerTypeEnum } from 'common/commonEnum';
import { insightAccessList, Insights } from 'config/Insights';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

interface GraphTabsProps {
  tabs: GraphTab[];
  activeTabId: string;
  isPro: boolean;
  onSelect: (tabId: string) => void;
}

export interface GraphTab {
  id: string; // format: `${insightId}-${group}`
  insightId: number;
  name: string;
  value: string;
  label: string;
  primary: InsightFilterEnum;
  secondary: InsightFilterEnum;
  order: number;
  group: string; 
  graphDescription: string;
  graphData: IGraphData[];
}

export const graphTabs: GraphTab[] = [];

Insights.filter((insight) => insight.enableGraphPulldown || insight.enableGraphButton).forEach(
  (insight) => {
    const groupMap = new Map<
      string,
      {
        group: string;
        items: typeof insight.graphData;
      }
    >();

    insight.graphData?.forEach((g) => {
      const group = g.group || 'default';
      if (!groupMap.has(group)) groupMap.set(group, { group, items: [] });
      groupMap.get(group)!.items.push(g);
    });

    groupMap.forEach(({ items }, group) => {
      const primary = items.find((g) => g.isPrimary);
      const secondary = items.find((g) => !g.isPrimary);

      if (primary && secondary) {
        const primaryLabel = primary.label;
        const secondaryLabel = secondary.label;

        let label = '';
        if (primaryLabel && secondaryLabel) {
          label = `${primaryLabel}/${secondaryLabel}`;
        } else if (primaryLabel) {
          label = primaryLabel;
        } else if (secondaryLabel) {
          label = secondaryLabel;
        }

        if (!primary.label && !secondary.label) {
          label = insight.graphButtonName;
        }
        if (!label) {
          label = insight.label || insight.graphButtonName || primary.value;
        }

        graphTabs.push({
          id: `${insight.insightId}-${group}`,
          insightId : insight.id,
          name: insight.graphButtonName || insight.label,
          value: insight.value,
          label,
          primary: primary.value as InsightFilterEnum,
          secondary: secondary.value as InsightFilterEnum,
          order: insight.enableGraphButtonOrder ?? 0,
          group,
          graphData: insight.graphData,
          graphDescription: insight.graphDescription || ''
        });
      }
    });
  }
);

graphTabs.sort((a, b) => a.order - b.order);
const isTabLocked = (tab: GraphTab, isPro: boolean): boolean => {
  const insight = Insights.find((i) => i.id === tab.insightId);
  if (!insight || !insight.graphPlayerTypeAccess) return false;
  const allowedPlayers = insight.graphPlayerTypeAccess;  
  return !isPro && !allowedPlayers.includes(playerTypeEnum.slotCheck);
};

const GraphTabs: React.FC<GraphTabsProps> = ({ tabs, activeTabId, onSelect, isPro }) => {
  const graphFilters = useSelector<IState>(
    (state) => state.app.persistedState.filterState[FilterTypeEnum.graphfilter as IFilterType]
  ) as ITabProperties;
  const { insight } = graphFilters;
  const InsightData = Insights.find((eachInsight) => eachInsight.value === insight);
  const history = useHistory();
  const location = useLocation();

  return (
    <div className="insight-selector-row">
      {tabs.map((tab) => {
        const locked = isTabLocked(tab, isPro);
        const isGraphButtonEnabled = InsightData?.enableGraphButton !== false;
        const handleClick = () => {
          if (locked) {
            history.push('/auth/stripe', { previousPage: location.pathname });
            return;
          }
          if (isGraphButtonEnabled) onSelect(tab.id);
        };
        return (
          <IonButton
            key={tab.id}
            className={`insight-button ${activeTabId === tab.id ? isPro ? 'selected-pro' : 'selected' : ''}`}
            onClick={handleClick}
            disabled={!isGraphButtonEnabled && !locked}
          >
            <span className="label-name">{tab.label} </span>
            {locked && <FontAwesomeIcon icon={faLock} className="icon-lock" />}
          </IonButton>
        );
      })}
    </div>
  );
};

export default GraphTabs;
